import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { CameraIcon } from "@heroicons/react/24/outline";
import { createColumnHelper } from "@tanstack/react-table";

import { IEbayMessage } from "../../models/EbayMessage";
import openInNewTab from "../../utils/openInNewTab";
import formatDateTime from "../../utils/formatDateTime";
import PagedTable from "../../components/shared/Tables/PagedTable";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import EbayMessageChain from "../../components/ebayMessaging/EbayMessageChain";

const colHelper = createColumnHelper<IEbayMessage>();

const ThumbnailCell = ({ message }: { message: IEbayMessage }) => {
  const [hovered, setHovered] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  if (!message.partImageUrl) {
    return <CameraIcon className="w-14 text-gray-400" />;
  }

  return (
    <>
      <span
        className="flex justify-center relative"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
      >
        <img
          className="min-w-16 max-w-28 h-auto object-contain rounded border border-gray-200"
          src={message.partImageUrl}
          alt={message.partTagNumber ?? "Part"}
        />
      </span>

      {hovered && (
        <div
          className="fixed pointer-events-none z-50"
          style={{ top: mousePos.y + 10, left: mousePos.x + 10 }}
        >
          <img
            src={message.partImageUrl}
            alt=""
            className="max-w-lg max-h-[32rem] border border-gray-300 shadow-lg rounded-md"
          />
        </div>
      )}
    </>
  );
};

const PartVehicleCell = ({ message }: { message: IEbayMessage }) => {
  if (!message.partId && !message.vehicleId) {
    return <span className="text-gray-400">—</span>;
  }

  const vehicleLabel = [message.vehicleVrm, message.vehicleDescription]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex flex-col gap-1 items-start">
      {message.partId && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openInNewTab(`/stock/${message.partId}`);
          }}
          className="text-primary-600 hover:underline text-left whitespace-nowrap"
        >
          {message.partTagNumber ? `Tag ${message.partTagNumber}` : "View part"}
        </button>
      )}
      {message.vehicleId && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openInNewTab(`/vehicles/${message.vehicleId}`);
          }}
          className="text-gray-600 hover:underline text-left text-xs whitespace-nowrap"
        >
          {vehicleLabel || "View vehicle"}
        </button>
      )}
    </div>
  );
};

export default function EbayMessages() {
  const { setInfo } = usePageNameContext();
  const [days, setDays] = useState(7);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [chosenMessage, setChosenMessage] = useState<IEbayMessage | undefined>();
  const [chainOpen, setChainOpen] = useState(false);

  useEffect(() => {
    setInfo({
      name: "View eBay Messages",
      desc: "View and manage messages to customers.",
    });
  }, []);

  // Drives both the query key (so it refetches) and the POST body the API reads.
  const additionalBody = useMemo(
    () => ({ daysToSearch: days, unreadOnly }),
    [days, unreadOnly]
  );

  const cols = useMemo(
    () => [
      colHelper.accessor("receiveDate", {
        header: "Received At",
        enableSorting: false,
        cell: (info) => (
          <div>
            <p className="whitespace-nowrap">{formatDateTime(info.getValue(), DateTime.DATETIME_SHORT)}</p>
            <p className="text-sm text-gray-600">{DateTime.fromISO(info.getValue()).toRelative()}</p>
          </div>
        ),
      }),
      colHelper.accessor("sendingUserID", {
        header: "Sender",
        enableSorting: false,
        cell: (info) => <span>{info.getValue() === "-99" ? "You" : info.getValue()}</span>,
      }),
      colHelper.display({
        id: "thumbnail",
        header: "",
        meta: { thClassName: "!px-2 w-32", tdClassName: "!px-2 w-32" },
        cell: (info) => <ThumbnailCell message={info.row.original} />,
      }),
      colHelper.accessor("itemTitle", {
        header: "Listing",
        enableSorting: false,
      }),
      colHelper.display({
        id: "partVehicle",
        header: "Part / Vehicle",
        cell: (info) => <PartVehicleCell message={info.row.original} />,
      }),
      colHelper.accessor("body", {
        header: "Text",
        enableSorting: false,
        cell: (info) => <span className="whitespace-pre-line">{info.getValue()}</span>,
      }),
      colHelper.accessor("read", {
        header: "Read",
        enableSorting: false,
        cell: (info) => (
          <span className="flex items-center justify-center">{info.getValue() === "True" ? "✔" : ""}</span>
        ),
      }),
    ],
    []
  );

  return (
    <div className="mt-4">
      <EbayMessageChain open={chainOpen} setOpen={setChainOpen} originMessage={chosenMessage} />

      <div className="flex gap-x-6 items-center">
        <div>
          <label>Days to display </label>
          <select onChange={(x) => setDays(parseInt(x.target.value))} value={days}>
            <option value={1}>1 Day</option>
            <option value={7}>1 Week</option>
            <option value={14}>2 Weeks</option>
            <option value={30}>1 Month</option>
          </select>
        </div>

        <div>
          <label>Unread Only </label>
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(x) => setUnreadOnly(x.target.checked)}
            className="cursor-pointer"
          />
        </div>
      </div>

      <PagedTable<IEbayMessage>
        url="/ebaymessaging/paged"
        columns={cols}
        // customUrlQuery puts the filters in the URL so the backend reads them and the response cache
        // keys on them; additionalBody carries the same values only to change the React Query key so a
        // toggle actually refetches (customUrlQuery alone isn't part of that key).
        customUrlQuery={`daysToSearch=${days}&unreadOnly=${unreadOnly}`}
        additionalBody={additionalBody}
        onRowClick={(message) => {
          setChosenMessage(message);
          setChainOpen(true);
        }}
        // PagedTable makes the first class important (`!bg-blue-50`); it's safelisted in tailwind.config.
        rowColor={(message) => (message.read !== "True" ? "bg-blue-50" : "")}
        showTotal
      />
    </div>
  );
}
